<template>
  <div class="dashboard-pages">
    <v-layout row mb-2 justify-center>
      <v-flex xs3 pa-2 ma-2 green lighten-3 v-show="showBalanceDetail">
        <p class="subheading font-weight-regular mb-0">
          {{ $t('Total Debit') }}:
          <strong>{{ accountReceivableInfo.total_debit | currency }} {{ systemCurrency }}</strong>
        </p>
      </v-flex>

      <v-flex xs3 pa-2 ma-2 red lighten-3 @click="showBalanceDetail = !showBalanceDetail">
        <p class="subheading font-weight-regular mb-0">
          {{ $t('Total Balance') }}:
          <strong>{{ accountReceivableInfo.total_balance | currency }} {{ systemCurrency }}</strong>
        </p>
      </v-flex>

      <v-flex xs3 pa-2 ma-2 yellow lighten-3 v-show="showBalanceDetail">
        <p class="subheading font-weight-regular mb-0">
          {{ $t('Total Credit') }}:
          <strong>{{ accountReceivableInfo.total_credit | currency }} {{ systemCurrency }}</strong>
        </p>
      </v-flex>
    </v-layout>

    <v-card class="pa-3">
      <v-layout row wrap justify-space-between>
        <dt-search-box
          :search="search"
          @typing="searchValue"
          @reset-datatable="resetDataTable()"
          @clear-search-field="search = null"
        />

        <v-layout row wrap justify-end class="pt-2 pb-2">
          <v-btn
            dark
            color="primary"
            v-if="hasPermissions(['receivable_payment_store'])"
            @click="dialogs.addDialog = true"
          >
            <v-icon>add</v-icon>
            {{ $t('datatable-add-new') }}
          </v-btn>
        </v-layout>
      </v-layout>
    </v-card>

    <v-card flat>
      <v-layout row wrap justify-end>
        <v-tooltip :left="!$vuetify.rtl" :right="$vuetify.rtl">
          <template v-slot:activator="{ on }">
            <v-btn
              style="margin: 5px 0px; height: 30px; width: 30px"
              :loading="loadThePrintBtn"
              flat
              fab
              v-on="on"
              color="primary"
              @click="getNoPaginatedData('print')"
            >
              <v-icon>print</v-icon>
            </v-btn>
          </template>
          <span>{{ $t(`Print Table`) }}</span>
        </v-tooltip>
        <v-tooltip :left="!$vuetify.rtl" :right="$vuetify.rtl">
          <template v-slot:activator="{ on }">
            <v-btn
              style="margin: 5px 20px 5px 10px; height: 30px; width: 30px"
              fab
              :loading="loadTheExportBtn"
              flat
              v-on="on"
              color="primary"
              @click="getNoPaginatedData('export')"
            >
              <v-icon>fa-file-excel</v-icon>
            </v-btn>
          </template>
          <span>{{ $t(`Export Table`) }}</span>
        </v-tooltip>
      </v-layout>
    </v-card>

    <v-card class="px-4 pb-4">
      <v-data-table
        v-show="isDatatable"
        :headers="headers"
        :items="resultData.data"
        :item-key="defaultSortBy"
        :search="search"
        :loading="loading"
        :pagination.sync="pagination"
        :rows-per-page-items="pagination.rowsPerPageItems"
        :total-items="resultData.total"
        :no-data-text="$t('No available data')"
        hide-actions
      >
        <template slot="items" slot-scope="props">
          <!--          <td>{{ props.item.receivable_payment_id }}</td>-->
          <td>{{ props.item.receivable_receive_auto_number }}</td>
          <td>{{ props.item.receive_date }}</td>
          <td>{{ props.item.amount | currency }}</td>
          <td>{{ props.item.discount | currency }}</td>
          <td v-if="hasActionsMenuPermissions()">
            <v-menu offset-y bottom :left="!$vuetify.rtl" :right="$vuetify.rtl" transition="slide-y-reverse-transition">
              <v-btn flat fab icon class="transparent py-2" slot="activator">
                <v-icon large class="grey--text actions-btn">more_vert</v-icon>
              </v-btn>
              <v-list>
                <v-tooltip
                  v-if="hasPermissions(['receivable_payment_show'])"
                  :left="!$vuetify.rtl"
                  :right="$vuetify.rtl"
                >
                  <v-list-tile slot="activator" @click="viewRecord(props.item.receivable_payment_id)">
                    <v-icon>chrome_reader_mode</v-icon>
                    <v-list-tile-content class="px-3">{{ $t('datatable-view') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t(`View Payment Information`) }}</span>
                </v-tooltip>

                <v-tooltip
                  v-if="hasPermissions(['receivable_payment_update']) && props.item.editable === '1'"
                  :left="!$vuetify.rtl"
                  :right="$vuetify.rtl"
                >
                  <v-list-tile slot="activator" @click="editRecord(props.item.receivable_payment_id)">
                    <v-icon>edit</v-icon>
                    <v-list-tile-content class="px-3">{{ $t('datatable-edit') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t(`Edit Payment Information`) }}</span>
                </v-tooltip>

                <v-divider v-if="hasPermissions(['receivable_payment_destroy']) && props.item.editable === '1'" />

                <v-tooltip
                  v-if="hasPermissions(['receivable_payment_destroy']) && props.item.editable === '1'"
                  :left="!$vuetify.rtl"
                  :right="$vuetify.rtl"
                >
                  <v-list-tile slot="activator" @click="showConfirmDeletion(props.item.receivable_payment_id)">
                    <v-icon class="red--text">delete</v-icon>
                    <v-list-tile-content class="red--text px-3">{{ $t('datatable-delete') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t('Delete Payment Record') }}</span>
                </v-tooltip>
              </v-list>
            </v-menu>
          </td>
        </template>

        <v-alert slot="no-results" :value="true" color="error" icon="warning">{{
          $t('Sorry, No results found for your search criteria')
        }}</v-alert>
      </v-data-table>

      <dt-pagination
        :resultData="resultData"
        :pagination="pagination"
        :perPageNumber="perPageNumber"
        @page-changed="pageChanged"
        @per-page-changed="resultsPerPageChanged"
      />
    </v-card>

    <add-receive
      :addDialog="dialogs.addDialog"
      :account="accountReceivableInfo"
      @reset-datatable="resetDataTable(), resetAllErrors(true)"
      @hidemodal=";(dialogs.addDialog = false), resetAllErrors(true)"
    />

    <edit-receive
      :editDialog="dialogs.editDialog"
      :accountId="Number(accountReceivableId)"
      @reset-datatable="resetDataTable()"
      @hidemodal=";(dialogs.editDialog = false), resetAllErrors(true)"
    />

    <view-receive :viewDialog="dialogs.viewDialog" @hidemodal="dialogs.viewDialog = false" />

    <confirm-deletion
      :deleteDialog="dialogs.deleteDialog"
      :btnDeleteLoading="btnDeleteLoading"
      @hidemodal="dialogs.deleteDialog = false"
      @confirm="deleteAction()"
    />

    <print-export-component
      :printExportDialog="dialogs.printExportDialog"
      :tableHeader="tableHeader"
      :tableBody="tableBody"
      :clickedButton="clickedButton"
      @hidemodal="dialogs.printExportDialog = false"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import * as helpers from '@/mixins/helperFunctions.js'
import Axios from 'axios'

export default {
  components: {
    dtSearchBox: () => import('@/views/_partials/AppSearchBox.vue'),
    dtPagination: () => import('@/views/_partials/AppDatatablePagination.vue'),
    viewReceive: () => import('./viewReceive.vue'),
    addReceive: () => import('./addReceive.vue'),
    editReceive: () => import('./editReceive.vue'),
    printExportComponent: () => import('@/views/_partials/printExportComponent.vue')
  },

  data() {
    return {
      accountReceivableId: Number(this.$route.query.account_receivable_id),
      name: this.$route.query.name,
      payment_id: 0,
      systemCurrency: localStorage.systemCurrency,
      dialogs: {
        viewDialog: false,
        addDialog: false,
        editDialog: false,
        deleteDialog: false,
        printExportDialog: false
      },
      tableBody: [],
      tableHeader: [],
      loadThePrintBtn: false,
      loadTheExportBtn: false,
      clickedButton: '',
      deleteId: 0,
      btnDeleteLoading: false,
      isDatatable: true,
      search: null,
      oldSearch: null,
      loading: false,
      defaultSortBy: 'receivable_payment_id',
      perPageNumber: 10,
      pagination: {
        sortBy: this.defaultSortBy,
        descending: true,
        page: 1,
        rowsPerPage: 10,
        totalItems: 1
      },
      oldPagination: {},
      headers: [
        // {
        //   text: this.$t('ID'),
        //   value: 'receivable_payment_id',
        //   align: 'left',
        //   sortable: true
        // },
        { text: this.$t('Code'), value: 'receivable_receive_auto_number' },
        { text: this.$t('Date'), value: 'receive_date' },
        {
          text: `${this.$t('Amount')} (${localStorage.systemCurrency})`,
          value: 'amount'
        },
        { text: this.$t('Discount'), value: 'discount' }
      ],
      allowGettingData: true,
      showBalanceDetail: false
    }
  },

  mounted() {
    this.setAppBarTitle({
      title: `${this.$t('Account Receivable Receives')}: <strong class="title-highlight">${this.name}</strong>`,
      icon: 'attach_money'
    })

    if (this.hasActionsMenuPermissions()) {
      this.headers.push({
        text: this.$t('datatable-actions'),
        value: '',
        sortable: false
      })
    }

    this.fetchRemittances()
  },

  computed: {
    ...mapGetters({
      resultData: 'receivablePayments/getPaymentsList',
      accountReceivableInfo: 'accountReceivable/getAccountReceivableInfo'
    })
  },

  watch: {
    pagination: {
      immediate: false,
      deep: false,
      handler() {
        let p = this.pagination
        let op = this.oldPagination
        if (p.sortBy !== op.sortBy || p.descending !== op.descending) {
          op.sortBy = p.sortBy
          op.descending = p.descending
          p.page = 1
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        } else if (this.allowGettingData || this.search !== this.oldSearch) {
          this.oldSearch = this.search
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        }
      }
    }
  },

  methods: {
    ...mapActions({
      resetAllErrors: 'errors/resetAllErrors',
      setErrorsList: 'errors/setErrorsList',
      updateResult: 'receivablePayments/updatePaymentsList',
      fetchViewInfo: 'receivablePayments/fetchPaymentInfo',
      fetchAccountReceivableInfo: 'accountReceivable/fetchAccountReceivableInfo',
      fetchRemittances: 'remittances/setFullRemittanceOfficesList',
      deleteRecord: 'receivablePayments/deletePayment'
    }),

    hasPermissions(permissions) {
      return helpers.userHasPermissions(permissions)
    },

    hasActionsMenuPermissions() {
      return helpers.userHasPermissions([
        'receivable_payment_show',
        'receivable_payment_update',
        'receivable_payment_destroy',
        'single_payment_payment_store'
      ])
    },

    getNoPaginatedData(clickedButton) {
      this.clickedButton = clickedButton
      if (clickedButton === 'print') {
        this.loadThePrintBtn = true
      } else if (clickedButton === 'export') {
        this.loadTheExportBtn = true
      }
      this.tableBody = []
      this.tableHeader = []
      this.tableHeader = this.headers
      this.tableHeader = this.tableHeader.filter(f => f.text !== 'Actions' && f.text !== 'الاجراءات')

      Axios.get(`receivable_payment_index/${this.accountReceivableId}`, {
        params: { is_paginated: 0, search: this.search }
      })
        .then(response => {
          this.tableBody = response.data.data
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = true
        })
        .catch(() => {
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = false
        })
    },

    updateDatatableResult() {
      this.loading = true
      let payload = {
        ...this.pagination,
        search: this.search,
        id: this.accountReceivableId
      }
      this.updateResult(payload)
        .then(() => {
          this.pagination.page = this.resultData.current_page
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
      this.fetchAccountReceivableInfo(this.accountReceivableId)
    },

    resetDataTable() {
      this.allowGettingData = true

      this.pagination = {
        sortBy: null,
        descending: true,
        page: 1,
        rowsPerPage: this.perPageNumber,
        totalItems: 1
      }
      this.search = this.oldSearch = null
    },

    resultsPerPageChanged(val) {
      this.perPageNumber = val
      this.pagination.rowsPerPage = val
      this.pagination.page = 1
      this.updateDatatableResult()
    },

    pageChanged(val) {
      this.pagination.page = val
      this.updateDatatableResult()
    },

    searchValue(val) {
      this.search = val
    },

    viewRecord(id) {
      this.fetchViewInfo(id)
        .then(() => {
          this.dialogs.viewDialog = true
        })
        .catch(() => {
          /* on error don't show dialog */
        })
    },

    editRecord(id) {
      this.fetchViewInfo(id)
        .then(() => {
          this.dialogs.editDialog = true
        })
        .catch(() => {
          /* on error don't show dialog */
        })
    },

    showConfirmDeletion(id) {
      this.deleteId = id
      this.dialogs.deleteDialog = true
    },

    deleteAction() {
      this.btnDeleteLoading = true
      this.deleteRecord(this.deleteId)
        .then(() => {
          this.deleteId = 0
          this.btnDeleteLoading = false
          this.dialogs.deleteDialog = false
          let payload = {
            ...this.pagination,
            search: this.search,
            id: this.accountReceivableId
          }
          this.updateResult(payload)
          this.fetchAccountReceivableInfo(this.accountReceivableId)
        })
        .catch(() => {
          this.deleteId = 0
          this.btnDeleteLoading = false
          this.dialogs.deleteDialog = false
        })
    }
  }
}
</script>

<style scoped>
.v-expansion-panel {
  margin-bottom: 20px;
}

.fadeInOut {
  animation: fadeInOut 2s;
}

@keyframes fadeInOut {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}
</style>
